import { useEffect } from "react";
import { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, Outlet, redirect, useMatches } from "@tanstack/react-router";
import { GraphQLClient } from "graphql-request";

import { REACT_QUERY_CLIENT } from "components/app_layout/AppProviders";
import { AppShell } from "components/app_layout/AppShell";
import { BetterAuthSessionGate } from "components/app_layout/BetterAuthSessionGate";
import Toaster from "components/app_layout/Toaster";
import TopLayerWrapper from "components/app_layout/TopLayerWrapper";
import HorizontalNav from "components/app_layout/vertical_nav_new/HorizontalNav";
import { DocumentUploadProgressProvider } from "components/content_library/DocumentUploadProgressProvider";
import { betterAuthSessionQueryOptions } from "lib/hooks/api/auth/betterAuthSessionQuery";
import { currentUserQueryOptions } from "lib/hooks/api/users/useCurrentUser";
import { needsSelfServeOnboarding } from "lib/onboarding/needsSelfServeOnboarding";
import { createStaleBundleGuard, isStaleBundleReloadHeld } from "lib/staleBundleReload";
import { removeTrailingSlash } from "lib/utils";

declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    pageName: string;
    /** If set, this label overrides the page names used in the auto-generated breadcrumb trail via useRouteBreadcrumbs(). */
    breadcrumbLabel?: string;
    /** If true, renders bare (no AppShell, no header). Use for print/embed pages. */
    noShell?: boolean;
    /**
     * If true, renders only the slim logo header (HorizontalNav) above the page — the
     * withAppLayout `hideMenuItems` equivalent used by auth/pre-login pages. Takes
     * precedence over noShell.
     */
    headerNav?: boolean;
    /**
     * With headerNav, make the logo non-clickable — the withAppLayout `disableLogoNav`
     * equivalent (e.g. onboarding, so users don't escape the flow via the logo).
     */
    headerNavDisableLogoNav?: boolean;
  }
}

// Router context type — available to all route `loader` functions via `context`.
// Routes use this to prefetch data: `loader: ({ context }) => context.queryClient.ensureQueryData(...)`
// and to make authenticated GraphQL requests via `context.graphQLClient` (a pre-authed client
// injected per-render by <AuthedRouter>, so loaders never touch module-level auth state).
//
// React Query v5 migration: replace this manual context wiring with `routerWithQueryClient`
// from `@tanstack/react-router-with-query`, which also handles automatic query invalidation
// on route transitions and dehydration/hydration for SSR.
export type RouterContext = {
  queryClient: QueryClient;
  graphQLClient: GraphQLClient;
};

export const TWO_FACTOR_SETUP_PATH = "/auth/2fa-setup";

/**
 * 2FA-setup enforcement: a user whose company mandates 2FA but who hasn't enrolled is sent to the
 * standalone setup page, then on to where they were heading. Self-gating — only Better Auth-migrated
 * users have a session with this flag, so it's a no-op for everyone else (no feature flag needed).
 * @param context
 * @param destination the path + query the user was navigating to
 */
async function ensure2FASetup(context: RouterContext, destination: string) {
  const betterAuthSession = await context.queryClient.ensureQueryData(
    betterAuthSessionQueryOptions(),
  );
  if (betterAuthSession?.twoFactorSetupRequired) {
    throw redirect({ to: TWO_FACTOR_SETUP_PATH, search: { redirect: destination } });
  }
}

function RootLayout() {
  const matches = useMatches();
  const pageName = matches.at(-1)?.staticData?.pageName ?? "";
  const noShell = matches.some((m) => m.staticData?.noShell);
  const headerNav = matches.some((m) => m.staticData?.headerNav);
  const headerNavDisableLogoNav = matches.some((m) => m.staticData?.headerNavDisableLogoNav);

  useEffect(() => {
    document.title = pageName ? `${pageName} | Stotles` : "Stotles";
  }, [pageName]);

  // Three layout modes: headerNav (slim logo header only), noShell (bare), or the full AppShell.
  let content;
  if (headerNav) {
    content = (
      <>
        <HorizontalNav disableLogoNav={headerNavDisableLogoNav} />
        <Outlet />
      </>
    );
  } else if (noShell) {
    content = <Outlet />;
  } else {
    content = (
      <AppShell trackingPageName={pageName}>
        <Outlet />
      </AppShell>
    );
  }

  // DocumentUploadProgressProvider is mounted at the root so upload progress survives navigation,
  // including across the shell boundary (e.g. an account-management integration sync started on a
  // bare page and continued under AppShell). RootLayout never unmounts, so the operations map
  // persists; AppShell, which remounts when the layout mode changes, must not own this state.
  //
  // The Toaster must be inside the router: toast actions can render SPA `to` links, which read
  // router context. TopLayerWrapper portals it to a body-level container, and context flows
  // through portals, so its DOM position is unaffected by living here.
  return (
    <DocumentUploadProgressProvider>
      <TopLayerWrapper>
        <Toaster />
      </TopLayerWrapper>
      <BetterAuthSessionGate>{content}</BetterAuthSessionGate>
    </DocumentUploadProgressProvider>
  );
}

// A pending mutation is often a save fired by the click itself (blur-to-save), so it must finish.
const staleBundleGuard = createStaleBundleGuard({
  canReload: () => !isStaleBundleReloadHeld() && REACT_QUERY_CLIENT.isMutating() === 0,
});

export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  staticData: { pageName: "" },
  // App-wide replacement for the old Rails `ApplicationController#ensure_self_serve_setup`
  // before_action: force unconfigured self-serve users into onboarding from any SPA page.
  // Runs for every route as the root of the tree.
  beforeLoad: async ({ context, location, preload }) => {
    // First, so a tab on an old frontend reloads before running any of this route's old code.
    await staleBundleGuard.beforeLoad({ location, preload });

    const onTwoFactorSetup = removeTrailingSlash(location.pathname) === TWO_FACTOR_SETUP_PATH;
    const onOnboarding = location.pathname.startsWith("/onboarding");

    // Skip the setup page (the destination) and onboarding (which takes precedence) to avoid loops.
    if (!onTwoFactorSetup && !onOnboarding) {
      await ensure2FASetup(context, location.pathname + location.searchStr);
    }

    // Guests (public report / shared links) have no authenticated session, so there's nothing
    // to enforce — mirrors the appToken guest gate and avoids a currentUser fetch that 401s.
    if (!window.userSignedIn) return;
    // Old guest-pass links render the retired page whatever state the account is in.
    if (location.pathname.startsWith("/guest_access")) return;
    // Never enforce on the onboarding page itself — that's the redirect destination, and
    // re-running the guard there would loop.
    if (onOnboarding) return;

    const { currentUser } = await context.queryClient.ensureQueryData(
      currentUserQueryOptions(context.graphQLClient),
    );
    if (needsSelfServeOnboarding(currentUser)) {
      // Carry the page they were heading to, so onboarding can send them there when done.
      // Home is the default anyway, and leaving it off lets the SEO signup destination apply.
      const destination = location.pathname + location.searchStr;
      throw redirect({
        to: "/onboarding",
        search: {
          status: "onboarding_required",
          ...(destination !== "/" && { redirect: destination }),
        },
      });
    }
  },
});
